import { Modal } from '@stormwater-software/lib-ui';
import React, { useState } from 'react';
import { toast } from 'react-toastify';
import { AssetStatus, assetStatusesApi } from '../../utils/api/asset';
import { ASSET_COLORS } from '../../utils/assets';
import classNames from 'classnames';
import CopyToDivisionModal from './CopyToDivisionModal';

interface IAssetStatusModalProps {
  divisionId: number;
  status?: AssetStatus | null;
  length: number;
  onSaved: () => void;
  onClose: () => void;
  onDelete?: () => void;
}

const AssetStatusModal: React.FC<IAssetStatusModalProps> = ({
  divisionId,
  status,
  length,
  onSaved,
  onClose,
  onDelete,
}) => {
  const isEdit = !!status;
  const canHandleEdit = isEdit ? status && status.division_id : true;
  const [name, setName] = useState(status?.name || '');
  const [color, setColor] = useState(status?.color || ASSET_COLORS[2]);
  const [isDefault, setIsDefault] = useState(!!status?.is_default);
  const [isRemoval, setIsRemoval] = useState(!!status?.is_removal);
  const [nameError, setNameError] = useState(false);
  const [saving, setSaving] = useState(false);
  const [showCopyModal, setShowCopyModal] = useState(false);

  const handleSave = async () => {
    if (!name.trim()) {
      setNameError(true);

      return;
    }
    setSaving(true);
    try {
      const data = {
        name,
        color,
        is_default: isDefault,
        is_removal: isRemoval,
        sort_order: isEdit ? status!.sort_order : length,
      };

      if (isEdit) {
        await assetStatusesApi.update(status!.id, data);
      } else {
        await assetStatusesApi.create(divisionId, data);
      }
      toast.success(`Status ${isEdit ? 'updated' : 'created'}`);
      onSaved();
      onClose();
    } catch (err) {
      toast.error(
        (err as any)?.response?.data?.message ||
          'Failed to save status. Please try again.',
      );
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal
      title={
        isEdit
          ? canHandleEdit
            ? `Edit: ${status!.name}`
            : `View ${status!.name}`
          : 'New Status'
      }
      show
      hideButtons
      handleClose={onClose}
      classes='asset-type-modal'
    >
      <div className='asset-status-form'>
        {isEdit && status!.division_id && (
          <div className='asset-type-modal__copy-row'>
            <button
              className='outline asset-type-modal__copy'
              onClick={() => setShowCopyModal(true)}
            >
              Copy To Division
            </button>
          </div>
        )}

        <label className='asset-config__label'>Name *</label>
        <input
          className={`asset-config__input${nameError ? ' has-error' : ''}`}
          value={name}
          autoFocus
          onChange={(e) => {
            setNameError(false);
            setName(e.target.value);
          }}
          disabled={!canHandleEdit}
        />
        {nameError && (
          <span className='asset-config__error'>Name is required</span>
        )}

        <label className='asset-config__label'>Color</label>
        <div className='asset-color-swatches'>
          {ASSET_COLORS.map((assetColor) => (
            <button
              key={assetColor}
              type='button'
              aria-label={`Select ${assetColor}`}
              className={classNames('asset-color-swatches__swatch', {
                'is-selected': color === assetColor,
              })}
              style={
                {
                  backgroundColor: assetColor,
                  '--swatch-color': assetColor,
                } as React.CSSProperties
              }
              onClick={() => setColor(assetColor)}
              disabled={!canHandleEdit}
            />
          ))}
        </div>
        <div className='asset-config__preview'>
          <span className='asset-config__hint'>Preview</span>
          <span
            className='asset-config__pill'
            style={{ backgroundColor: `${color}22`, color }}
          >
            {name || 'Status Name'}
          </span>
        </div>

        <label className='asset-config__checkbox'>
          <input
            type='checkbox'
            checked={isDefault}
            onChange={(e) => setIsDefault(e.target.checked)}
            disabled={!canHandleEdit}
          />
          <span>Default status for new assets</span>
        </label>

        <label className='asset-config__checkbox'>
          <input
            type='checkbox'
            checked={isRemoval}
            onChange={(e) => setIsRemoval(e.target.checked)}
            disabled={!canHandleEdit}
          />
          <span>Removes the asset (archives it)</span>
        </label>

        <div className='asset-type-modal__footer'>
          {isEdit && onDelete && (
            <button
              className='outline warn asset-config__delete'
              onClick={onDelete}
              disabled={!canHandleEdit}
            >
              Delete Status
            </button>
          )}
          <span className='spacer' />
          <button className='outline' onClick={onClose}>
            Cancel
          </button>
          <button
            className='primary'
            onClick={handleSave}
            disabled={saving || !canHandleEdit}
          >
            {saving ? 'Saving…' : 'Save Changes'}
          </button>
        </div>
      </div>

      {showCopyModal && status && (
        <CopyToDivisionModal
          title='Copy Status to Division'
          successMessage='Status copied'
          onCopy={(divisionIds) =>
            assetStatusesApi.copy(divisionIds, [status.id])
          }
          onClose={() => setShowCopyModal(false)}
        />
      )}
    </Modal>
  );
};

export default AssetStatusModal;
